import { Building2, CheckCircle2, Eye, EyeOff, ImagePlus, Images, KeyRound, LoaderCircle, LogIn, Percent, RefreshCw, Search, ShieldAlert, Upload, Users, X } from "lucide-react";
import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import type { User } from "../types";

type Slot = "main" | "gallery_01" | "gallery_02";
type AdminTab = "products" | "users" | "commercial";

interface AdminProduct {
  id: string;
  sku: string;
  external_code: string | null;
  name: string;
  category: string | null;
  active: boolean;
  image_url: string | null;
  gallery_images: string[];
  synced_at: string;
}

interface AdminUser {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface CommercialLink {
  user_id: string; company_id: string; representative_name: string; representative_email: string;
  cnpj: string; legal_name: string; trade_name: string | null; status: string;
  show_invoices: boolean; show_billing: boolean;
}

interface ListResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

const slots: { id: Slot; label: string }[] = [
  { id: "main", label: "Principal" },
  { id: "gallery_01", label: "Galeria 01" },
  { id: "gallery_02", label: "Galeria 02" }
];

function slotUrl(product: AdminProduct, slot: Slot) {
  if (slot === "main") return product.image_url;
  return product.gallery_images?.[slot === "gallery_01" ? 0 : 1] || null;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

export function AdminPage() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [tab, setTab] = useState<AdminTab>("products");
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [portalUsers, setPortalUsers] = useState<AdminUser[]>([]);
  const [commercialLinks, setCommercialLinks] = useState<CommercialLink[]>([]);
  const [commissionPercent, setCommissionPercent] = useState("0");
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploading, setUploading] = useState("");
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);

  const passwordValid = useMemo(() =>
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^A-Za-z0-9]/.test(password), [password]);

  const loadProducts = useCallback(async (term = "") => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ limit: "60" });
      if (term.trim()) params.set("search", term.trim());
      const result = await api<ListResponse<AdminProduct>>(`/api/admin/products?${params}`);
      setProducts(result.data); setTotal(result.total);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Não foi possível carregar os produtos");
    } finally { setLoading(false); }
  }, []);

  const loadUsers = useCallback(async (term = "") => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (term.trim()) params.set("search", term.trim());
      const result = await api<ListResponse<AdminUser>>(`/api/admin/users?${params}`);
      setPortalUsers(result.data); setTotal(result.total);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Não foi possível carregar os usuários");
    } finally { setLoading(false); }
  }, []);

  const loadCommercial = useCallback(async (term = "") => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams(); if (term.trim()) params.set("search", term.trim());
      const [links, settings] = await Promise.all([
        api<{ data: CommercialLink[]; total: number }>(`/api/admin/commercial/links?${params}`),
        api<{ data: { commission_percent: number } }>("/api/admin/commercial/settings")
      ]);
      setCommercialLinks(links.data || []); setTotal(links.total || 0);
      setCommissionPercent(String(Number(settings.data.commission_percent || 0)));
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível carregar as configurações comerciais"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api<{ user: User | null }>("/api/auth/me")
      .then(result => setUser(result.user))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user?.isAdmin) return;
    const timer = window.setTimeout(() => {
      if (tab === "products") loadProducts(search);
      else if (tab === "users") loadUsers(search);
      else loadCommercial(search);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, tab, user?.isAdmin, loadProducts, loadUsers, loadCommercial]);

  function changeTab(nextTab: AdminTab) {
    setTab(nextTab);
    setSearch("");
    setError("");
    setSuccess("");
    setTotal(0);
  }

  async function uploadImage(product: AdminProduct, slot: Slot, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const key = `${product.id}:${slot}`;
    setUploading(key); setError(""); setSuccess("");
    try {
      const body = new FormData(); body.append("file", file);
      const response = await fetch(`/api/admin/products/${product.id}/images/${slot}`, {
        method: "POST", credentials: "same-origin", body
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || "Falha no envio da imagem");
      setProducts(current => current.map(item => item.id === product.id ? { ...item, ...result.data } : item));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Falha no envio da imagem");
    } finally { setUploading(""); }
  }

  async function saveCommission() {
    setLoading(true); setError("");
    try {
      await api("/api/admin/commercial/settings", { method: "PATCH", body: JSON.stringify({ commission_percent: Number(commissionPercent) }) });
      setSuccess("Percentual global de comissão atualizado.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível salvar o percentual"); }
    finally { setLoading(false); }
  }

  async function updateVisibility(link: CommercialLink, field: "show_invoices" | "show_billing", value: boolean) {
    const next = { ...link, [field]: value };
    setCommercialLinks(current => current.map(item => item.user_id === link.user_id && item.company_id === link.company_id ? next : item));
    try {
      await api("/api/admin/commercial/links/visibility", { method: "PATCH", body: JSON.stringify({
        user_id: link.user_id, company_id: link.company_id,
        show_invoices: next.show_invoices, show_billing: next.show_billing
      }) });
    } catch (reason) {
      setCommercialLinks(current => current.map(item => item.user_id === link.user_id && item.company_id === link.company_id ? link : item));
      setError(reason instanceof Error ? reason.message : "Não foi possível atualizar a visibilidade");
    }
  }

  function openPasswordReset(target: AdminUser) {
    setResetTarget(target);
    setPassword("");
    setConfirmation("");
    setShowPassword(false);
    setError("");
    setSuccess("");
  }

  function closePasswordReset() {
    if (resetting) return;
    setResetTarget(null);
    setPassword("");
    setConfirmation("");
  }

  async function resetPassword(event: FormEvent) {
    event.preventDefault();
    if (!resetTarget || !passwordValid || password !== confirmation) return;
    setResetting(true); setError("");
    try {
      await api<{ message: string }>(`/api/admin/users/${resetTarget.id}/password`, {
        method: "PATCH",
        body: JSON.stringify({ password })
      });
      const targetName = resetTarget.name;
      closePasswordReset();
      setResetTarget(null);
      setPassword("");
      setConfirmation("");
      setSuccess(`Senha de ${targetName} redefinida. As sessões anteriores foram encerradas.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Não foi possível redefinir a senha");
    } finally { setResetting(false); }
  }

  if (user === undefined) return <AdminState icon={<LoaderCircle className="spin" />} title="Verificando acesso" text="Aguarde um instante." />;
  if (!user) return <AdminState icon={<LogIn />} title="Entre para administrar" text="Use uma conta autorizada para administrar o catálogo."><Link className="button primary" to="/entrar">Entrar</Link></AdminState>;
  if (!user.isAdmin) return <AdminState icon={<ShieldAlert />} title="Acesso não autorizado" text={`O usuário ${user.email} não está na lista de administradores.`} />;

  const isProducts = tab === "products";
  const isUsers = tab === "users";

  return (
    <section className="section admin-page">
      <div className="admin-heading">
        <div>
          <span className="eyebrow">Painel administrativo</span>
          <h1>{isProducts ? "Imagens dos produtos" : isUsers ? "Usuários do portal" : "Comercial e representantes"}</h1>
          <p>{isProducts
            ? "Anexe a imagem principal e até duas imagens de galeria. Os arquivos são convertidos automaticamente para WebP."
            : isUsers ? "Consulte os cadastros e defina uma nova senha quando um usuário solicitar recuperação de acesso."
            : "Controle documentos visíveis por CNPJ e o percentual global de comissão."}</p>
        </div>
        <button className="button secondary dark" onClick={() => isProducts ? loadProducts(search) : isUsers ? loadUsers(search) : loadCommercial(search)} disabled={loading}>
          <RefreshCw size={17} className={loading ? "spin" : ""} /> Atualizar
        </button>
      </div>

      <nav className="admin-tabs" aria-label="Áreas do painel">
        <button className={isProducts ? "active" : ""} onClick={() => changeTab("products")}><Images size={18} /> Produtos</button>
        <button className={isUsers ? "active" : ""} onClick={() => changeTab("users")}><Users size={18} /> Usuários</button>
        <button className={tab === "commercial" ? "active" : ""} onClick={() => changeTab("commercial")}><Building2 size={18} /> Comercial</button>
      </nav>

      <div className="admin-toolbar">
        <label className="search-box">
          <Search size={19} />
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={isProducts ? "Nome, SKU ou código legado" : isUsers ? "Nome, e-mail ou telefone" : "Representante, e-mail, CNPJ ou empresa"}
          />
        </label>
        <span>{loading ? "Carregando..." : `${total} ${isProducts ? "produtos" : isUsers ? "usuários" : "vínculos"} encontrados`}</span>
      </div>

      {error && <div className="form-error admin-error">{error}</div>}
      {success && <div className="admin-success"><CheckCircle2 size={18} /> {success}</div>}

      {isProducts ? (
        <>
          <div className="admin-product-list">
            {products.map(product => (
              <article className="admin-product" key={product.id}>
                <div className="admin-product-info">
                  <div className="admin-thumb">{product.image_url ? <img src={`${product.image_url}?preview=${encodeURIComponent(product.synced_at)}`} alt="" /> : <Images />}</div>
                  <div><span>{product.category || "Sem categoria"}</span><h2>{product.name}</h2><p>SKU <strong>{product.sku}</strong>{product.external_code && <> · Cód. legado <strong>{product.external_code}</strong></>}</p></div>
                </div>
                <div className="image-slots">
                  {slots.map(slot => {
                    const url = slotUrl(product, slot.id);
                    const busy = uploading === `${product.id}:${slot.id}`;
                    return <label className={url ? "image-slot filled" : "image-slot"} key={slot.id}>
                      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={event => uploadImage(product, slot.id, event)} disabled={Boolean(uploading)} />
                      <div className="slot-preview">{url ? <img src={`${url}?preview=${Date.now()}`} alt={slot.label} /> : <ImagePlus />}</div>
                      <span>{slot.label}</span>
                      <small>{busy ? <><LoaderCircle size={13} className="spin" /> Enviando...</> : <><Upload size={13} /> {url ? "Substituir" : "Anexar"}</>}</small>
                    </label>;
                  })}
                </div>
              </article>
            ))}
          </div>
          {!loading && products.length === 0 && <div className="state-card"><Images /><h3>Nenhum produto encontrado</h3><p>Verifique a pesquisa ou execute a sincronização do catálogo.</p></div>}
        </>
      ) : isUsers ? (
        <>
          <div className="admin-user-list">
            {portalUsers.map(portalUser => (
              <article className="admin-user" key={portalUser.id}>
                <div className="admin-user-avatar" aria-hidden="true">{portalUser.name.trim().charAt(0).toUpperCase()}</div>
                <div className="admin-user-main">
                  <div className="admin-user-title">
                    <h2>{portalUser.name}</h2>
                    <span className={portalUser.active ? "status-pill approved" : "status-pill blocked"}>{portalUser.active ? "Ativo" : "Inativo"}</span>
                  </div>
                  <p>{portalUser.email}</p>
                  <small>{portalUser.phone || "Telefone não informado"} · Cadastro em {formatDate(portalUser.created_at)}</small>
                </div>
                <button className="button secondary dark admin-reset-button" onClick={() => openPasswordReset(portalUser)}>
                  <KeyRound size={17} /> Redefinir senha
                </button>
              </article>
            ))}
          </div>
          {!loading && portalUsers.length === 0 && <div className="state-card"><Users /><h3>Nenhum usuário encontrado</h3><p>Revise o termo pesquisado.</p></div>}
        </>
      ) : (
        <div className="commercial-admin">
          <section className="commission-setting"><div><Percent /><span><strong>Percentual global de comissão</strong><small>Aplicado ao valor efetivamente faturado de pedidos válidos.</small></span></div><label><input type="number" min="0" max="100" step="0.01" value={commissionPercent} onChange={event => setCommissionPercent(event.target.value)} /><span>%</span></label><button className="button primary" disabled={loading} onClick={saveCommission}>Salvar percentual</button></section>
          <div className="commercial-link-list">{commercialLinks.map(link => <article key={`${link.user_id}:${link.company_id}`}><div><span className="company-document">{link.cnpj}</span><h2>{link.trade_name || link.legal_name}</h2><p>{link.representative_name} · {link.representative_email}</p></div><div className="visibility-toggles"><label><input type="checkbox" checked={link.show_invoices} onChange={event => updateVisibility(link, "show_invoices", event.target.checked)} /> Notas faturadas</label><label><input type="checkbox" checked={link.show_billing} onChange={event => updateVisibility(link, "show_billing", event.target.checked)} /> Boletos e financeiro</label></div></article>)}</div>
          {!loading && commercialLinks.length === 0 && <div className="state-card"><Building2 /><h3>Nenhum vínculo encontrado</h3><p>Revise o termo pesquisado.</p></div>}
        </div>
      )}

      {resetTarget && (
        <div className="admin-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closePasswordReset(); }}>
          <div className="admin-modal" role="dialog" aria-modal="true" aria-labelledby="reset-password-title">
            <button className="admin-modal-close" onClick={closePasswordReset} disabled={resetting} aria-label="Fechar"><X /></button>
            <div className="admin-modal-icon"><KeyRound /></div>
            <span className="eyebrow">Recuperação de acesso</span>
            <h2 id="reset-password-title">Redefinir senha</h2>
            <p>Defina uma nova senha para <strong>{resetTarget.name}</strong> ({resetTarget.email}). Ao confirmar, todas as sessões atuais desse usuário serão encerradas.</p>
            <form className="admin-reset-form" onSubmit={resetPassword}>
              <label>
                Nova senha
                <span className="password-field">
                  <input autoFocus type={showPassword ? "text" : "password"} value={password} onChange={event => setPassword(event.target.value)} minLength={8} maxLength={128} autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}>{showPassword ? <EyeOff /> : <Eye />}</button>
                </span>
              </label>
              <small className={password && !passwordValid ? "field-invalid" : passwordValid ? "field-valid" : ""}>Use 8 ou mais caracteres, com maiúscula, minúscula, número e símbolo.</small>
              <label>
                Confirmar nova senha
                <input type={showPassword ? "text" : "password"} value={confirmation} onChange={event => setConfirmation(event.target.value)} minLength={8} maxLength={128} autoComplete="new-password" />
              </label>
              {confirmation && password !== confirmation && <small className="field-invalid">As senhas não coincidem.</small>}
              <div className="admin-modal-actions">
                <button type="button" className="button secondary dark" onClick={closePasswordReset} disabled={resetting}>Cancelar</button>
                <button type="submit" className="button primary" disabled={resetting || !passwordValid || password !== confirmation}>
                  {resetting ? <LoaderCircle size={17} className="spin" /> : <KeyRound size={17} />}
                  {resetting ? "Redefinindo..." : "Confirmar nova senha"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

function AdminState({ icon, title, text, children }: { icon: React.ReactNode; title: string; text: string; children?: React.ReactNode }) {
  return <section className="admin-state"><div>{icon}<h1>{title}</h1><p>{text}</p>{children}</div></section>;
}

